// src/utils/assignableUsers.js
//
// One definition of "who can a lead be assigned to".
//
// There were three, and they disagreed. The canonical CRM list (usePracticeStaff)
// allows only TCO and USER roles, while the Dentally booking screens instead
// excluded ADMIN and SUPPORT — which KEEPS the PRACTITIONER role. Dentally
// practitioners are synced onto PracticeUser rows by email
// (apps/dentally/views.py "Sync Dentally practitioners to PracticeUser"), so the
// Dentally flows offered clinicians as assignees who cannot be chosen anywhere
// else in the CRM, and saved them: filterPracticeUserSelections validates a
// selection against the same list that was displayed, so it never caught it.
//
// Two of the three also dropped guards the canonical list has:
//   - DentallyAvailabilityStep did not check `is_enabled`, so a disabled user
//     was still offered.
//   - None checked `user.is_active !== false`. usePracticeStaff documents what
//     that costs: the bulk-assignees write 400s with "invalid_user_ids" and
//     silently drops the lead-propagation dialog (seen on pre-prod, practice 36).
//
// This exports the PREDICATE rather than a ready-made list on purpose. The
// Dentally screens read a PRACTICE-SCOPED selector and the shared hook reads the
// unscoped slice; replacing their source with the hook would unify the rule at
// the cost of reintroducing a cross-practice leak. Unify the test, keep the
// source each caller already uses.

/** Roles a lead may be assigned to. Practitioners book; TCOs and users own leads. */
export const ASSIGNABLE_ROLES = ['TCO', 'USER'];

/**
 * Can this practice user be an assignee on a lead?
 *
 * `is_active !== false` rather than `=== true`: the backend field arrived in a
 * later PR, so an older payload omits it and the user must be kept (fail-open).
 * Only an EXPLICITLY deactivated account is excluded.
 */
export const isAssignableUser = (practiceUser) => Boolean(
  practiceUser?.is_enabled
  && ASSIGNABLE_ROLES.includes(practiceUser?.role?.toUpperCase())
  && !practiceUser?.user?.is_superuser
  && practiceUser?.user?.is_active !== false
);

/** Convenience for callers that already hold a practice-scoped array. */
export const selectAssignableUsers = (practiceUsers = []) => practiceUsers.filter(isAssignableUser);

/** "First Last", or a stable fallback so a nameless row never renders blank. */
export const assignableUserName = (practiceUser) => {
  const user = practiceUser?.user || {};
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || 'User';
};
